import React, { useEffect, useMemo, useState, useContext } from "react";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "../../components/Navbar";
import Swal from "sweetalert2";
import { resolveAssetUrl } from "../../api/config";
import SearchableSelect from "../../components/common/SearchableSelect";

const normalizeRegistration = (value) =>
  String(value || "").trim().toLowerCase() === "open" ? "Open" : "Closed";

const formatIsoDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

const formatEventDateRange = (startDate, endDate) => {
  const start = String(startDate || "").trim();
  const end = String(endDate || startDate || "").trim();
  if (!start && !end) return "-";
  if (!start) return end;
  if (!end || end === start) return start;
  return `${start} to ${end}`;
};

const ManageEvents = () => {
  const { role, user } = useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOpen, setShowOpen] = useState(true);
  const [showClosed, setShowClosed] = useState(true);
  const [sports, setSports] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [sponsorLogoPreview, setSponsorLogoPreview] = useState(null);

  const [formData, setFormData] = useState({
    id: null,
    sport_id: "",
    title: "",
    start_date: "",
    end_date: "",
    date: "",
    event_end_date: "",
    end_time: "",
    location: "",
    description: "",
    convenor_name: "",
    sponsor_name: "",
    registration_fee: "",
    escort_fee: "",
    max_games_per_player: 3,
    event_level: "District",
    target_age_category: "all",
    target_gender: "all",
    district: "",
    district_id: "",
    registration: "Closed",
    image: null,
    sponsor_logo: null,
  });

  // ✅ Load events and sports
  useEffect(() => {
    if (editing) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    const loadData = async () => {
      try {
        const [eventRes, sportRes] = await Promise.all([
          API.fetchAdminEvents(),
          API.getSports(),
        ]);
        setEvents(eventRes.data || eventRes || []);
        setSports(sportRes.data || sportRes || []);

        // District role does not need district options (district is fixed to login district).
        if ((role || "").toLowerCase() !== "district") {
          try {
            const districtRes = await API.fetchDistrictOptions();
            setDistrictOptions(Array.isArray(districtRes) ? districtRes : []);
          } catch {
            setDistrictOptions([]);
          }
        } else {
          setDistrictOptions([]);
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to load events or sports.",
        });
      }
    };
    loadData();
  }, [editing, role]);

  useEffect(() => {
    if ((role || "").toLowerCase() === "district") {
      setFormData((prev) => ({
        ...prev,
        event_level: "District",
        district: user?.district || prev.district || "",
        district_id: user?.district_id ? String(user.district_id) : prev.district_id || "",
      }));
    }
  }, [role, user?.district, user?.district_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "end_time") {
        let next = String(value || "").trim();
        if (/^\d{2}:\d{2}$/.test(next)) {
          next = `${next}:00`;
        }
        return { ...prev, end_time: next };
      }
      if (name === "district_id") {
        const selected = districtOptions.find((d) => String(d.id ?? "") === value);
        return {
          ...prev,
          district_id: value,
          district: selected?.name || "",
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    let [hours, minutes, seconds] = time.split(':');
    hours = hours?.padStart(2, '0') || '00';
    minutes = minutes?.padStart(2, '0') || '00';
    seconds = seconds?.padStart(2, '0') || '00';
    return `${hours}:${minutes}:${seconds}`;
  };

  const filteredEvents = useMemo(() => {
    const query = String(searchTerm || "").trim().toLowerCase();
    return (Array.isArray(events) ? events : []).filter((event) => {
      const status = normalizeRegistration(event.registration);
      if (!showOpen && status === "Open") return false;
      if (!showClosed && status === "Closed") return false;

      if (!query) return true;
      const haystack = [
        event.title,
        event.sport_name,
        event.event_level,
        event.target_age_category,
        event.target_gender,
        event.district,
        event.convenor_name,
        event.sponsor_name,
        event.event_date,
        event.date,
        event.event_end_date,
      ]
        .map((item) => String(item || "").toLowerCase())
        .join(" ");

      return haystack.includes(query);
    });
  }, [events, searchTerm, showOpen, showClosed]);

  const openCount = useMemo(
    () =>
      (Array.isArray(events) ? events : []).filter(
        (event) => normalizeRegistration(event.registration) === "Open"
      ).length,
    [events]
  );

  const closedCount = useMemo(
    () =>
      (Array.isArray(events) ? events : []).filter(
        (event) => normalizeRegistration(event.registration) === "Closed"
      ).length,
    [events]
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, image: file }));

    if (file) {
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSponsorLogoChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, sponsor_logo: file || null }));
    if (file) {
      setSponsorLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { sport_id, title, date, event_end_date, district, district_id } = formData;
    
    if (!sport_id || !title || !date) {
      return Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Please fill in sport, title and event date.",
      });
    }

    const today = new Date().toISOString().split("T")[0];
    if (date && new Date(date) < new Date(today)) {
      return Swal.fire({
        icon: "warning",
        title: "Invalid Date",
        text: "Event date cannot be in the past.",
      });
    }

    if (event_end_date && date && new Date(event_end_date) < new Date(date)) {
      return Swal.fire({
        icon: "warning",
        title: "Invalid Date Range",
        text: "Event end date cannot be before event start date.",
      });
    }

    if (!String(district || "").trim() && !String(district_id || "").trim()) {
      return Swal.fire({
        icon: "warning",
        title: "District Required",
        text: "Please select district for this event.",
      });
    }

    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (k === "image") {
          if (v) fd.append("image", v);
        } else {
          if (v !== null && v !== undefined) fd.append(k, v);
        }
      });

      console.log('🔍 FormData contents:');
      for (let [key, value] of fd.entries()) {
        console.log(key, value);
      }

      const confirm = await Swal.fire({
        title: editing ? "Update Event?" : "Create Event?",
        text: editing
          ? "Changes will be saved permanently."
          : "A new event will be created.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: editing ? "Update" : "Create",
        cancelButtonText: "Cancel",
      });

      if (!confirm.isConfirmed) {
        setLoading(false);
        return;
      }
      if (editing) {
        await API.updateEvent(formData.id, fd);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Event updated successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await API.createEvent(fd);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Event created successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      // Reset form
      setEditing(false);
      setFormData({
        id: null, sport_id: "", title: "", start_date: "", end_date: "", 
        date: "", event_end_date: "", end_time: "", location: "", description: "", convenor_name: "", sponsor_name: "",
        registration_fee: "", escort_fee: "", max_games_per_player: 3,
        event_level: "District",
        target_age_category: "all",
        target_gender: "all",
        district: (role || "").toLowerCase() === "district" ? (user?.district || "") : "",
        district_id: (role || "").toLowerCase() === "district" ? String(user?.district_id || "") : "",
        registration: "Closed", image: null, sponsor_logo: null
      });
      setImagePreview(null);
      setSponsorLogoPreview(null);

      // Refresh list
      const refreshed = await API.fetchAdminEvents();
      setEvents(refreshed.data || refreshed || []);

    } catch (err) {
      console.error("Error saving event:", err);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: err.response?.data?.message || "Error saving event.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (ev) => {
    const result = await Swal.fire({
      title: "Edit Event?",
      text: "You are about to modify this event.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Edit",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;
    
    setFormData({
      id: ev.id,
      sport_id: ev.sport_id ? String(ev.sport_id) : "",
      title: ev.title || "",
      start_date: formatIsoDate(ev.start_date),
      end_date: formatIsoDate(ev.end_date),
      date: formatIsoDate(ev.event_date || ev.date),
      event_end_date: formatIsoDate(ev.event_end_date || ev.event_date || ev.date),
      end_time: formatTime(ev.end_time) || "",
      location: ev.location || "",
      description: ev.description || "",
      convenor_name: ev.convenor_name || "",
      sponsor_name: ev.sponsor_name || "",
      registration_fee: ev.registration_fee ?? "",
      escort_fee: ev.escort_fee ?? "",
      max_games_per_player: ev.max_games_per_player ?? 3,
      event_level: ev.event_level || "District",
      target_age_category: ev.target_age_category || "all",
      target_gender: ev.target_gender || "all",
      district: ev.district || "",
      district_id: ev.district_id ? String(ev.district_id) : "",
      registration: normalizeRegistration(ev.registration),
      image: null,
      sponsor_logo: null,
    });

    setImagePreview(
      ev.image
        ? resolveAssetUrl(ev.image, { folder: "events" })
        : null
    );
    setSponsorLogoPreview(
      ev.sponsor_logo
        ? resolveAssetUrl(ev.sponsor_logo, { folder: "events/sponsors" })
        : null
    );

    setEditing(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await API.deleteEvent(id);
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Event has been deleted.",
          timer: 1500,
          showConfirmButton: false,
        });
        setEvents((prev) => prev.filter((e) => e.id !== id));
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Error deleting event.",
        });
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    const action = currentStatus === "Open" ? "Close" : "Open";
    const result = await Swal.fire({
      title: `Confirm ${action}?`,
      text: `Are you sure you want to ${action.toLowerCase()} this event?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: currentStatus === "Open" ? "#3085d6" : "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: action,
    });

    if (result.isConfirmed) {
      try {
        await API.put(`/admin/events/${id}/toggle`);
        const nextStatus = normalizeRegistration(currentStatus) === "Open" ? "Closed" : "Open";
        setEvents((prev) =>
          prev.map((event) =>
            event.id === id ? { ...event, registration: nextStatus } : event
          )
        );
        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: `Event ${action.toLowerCase()}d successfully!`,
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Error toggling event status.",
        });
      }
    }
  };

  if (!["admin", "secretary", "district"].includes((role || "").toLowerCase())) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Unauthorized Access
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-sky-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-sky-700">Event Studio</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Manage Events</h1>
              <p className="text-sm text-slate-600 mt-1">
                Create event blocks, registration windows, and publication status from one workspace.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Total</p>
                <p className="text-lg font-semibold text-slate-900">{events.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Visible</p>
                <p className="text-lg font-semibold text-slate-900">{filteredEvents.length}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Open</p>
                <p className="text-lg font-semibold text-emerald-800">{openCount}</p>
              </div>
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-rose-700">Closed</p>
                <p className="text-lg font-semibold text-rose-800">{closedCount}</p>
              </div>
            </div>
          </div>
        </section>

        {/* 📝 Event Form */}
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_280px] mb-8">
        <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 rounded-2xl border border-slate-200 shadow-md mb-0">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Sport</label>
              <SearchableSelect 
                name="sport_id" 
                value={formData.sport_id} 
                onChange={handleChange} 
                required 
                className="border p-2 rounded w-full"
              >
                <option value="">Select Sport</option>
                {Array.isArray(sports) && sports.map((sport) => (
                  <option key={sport.id} value={sport.id}>{sport.name}</option>
                ))}
              </SearchableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Event Title</label>
              <input 
                type="text" 
                name="title" 
                placeholder="Event Title" 
                value={formData.title} 
                onChange={handleChange} 
                className="border p-2 rounded w-full" 
                required 
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Registration Start Date</label>
              <input 
                type="date" 
                name="start_date" 
                value={formData.start_date} 
                onChange={handleChange} 
                className="border p-2 rounded w-full" 
              />
              <small className="text-xs text-gray-500">Start date may be in the past</small>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Registration End Date & Time</label>
              <div className="flex gap-2">
                <input 
                  type="date" 
                  name="end_date" 
                  value={formData.end_date} 
                  onChange={handleChange} 
                  className="border p-2 rounded w-full" 
                />
                <input 
                  type="time"
                  step="1"
                  name="end_time" 
                  value={formatTime(formData.end_time)} 
                  onChange={handleChange}
                  className="border p-2 rounded w-36"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Event Date</label>
              <input 
                type="date" 
                name="date" 
                value={formData.date} 
                onChange={handleChange} 
                className="border p-2 rounded w-full" 
                required 
              />
              <small className="text-xs text-gray-500">Event date must not be in the past</small>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Event End Date (optional)</label>
              <input
                type="date"
                name="event_end_date"
                value={formData.event_end_date}
                onChange={handleChange}
                min={formData.date || undefined}
                className="border p-2 rounded w-full"
              />
              <small className="text-xs text-gray-500">Leave empty for one-day event</small>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <input 
                type="text" 
                name="location" 
                placeholder="Location" 
                value={formData.location} 
                onChange={handleChange} 
                className="border p-2 rounded w-full" 
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Registration Fee (₹)</label>
              <input 
                type="number" 
                name="registration_fee" 
                placeholder="Registration Fee" 
                value={formData.registration_fee} 
                onChange={handleChange} 
                className="border p-2 rounded w-full" 
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Escort Fee (₹)</label>
              <input 
                type="number" 
                name="escort_fee" 
                placeholder="Escort Fee" 
                value={formData.escort_fee} 
                onChange={handleChange} 
                className="border p-2 rounded w-full" 
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Convenor Name</label>
              <input
                type="text"
                name="convenor_name"
                placeholder="Convenor Name"
                value={formData.convenor_name}
                onChange={handleChange}
                className="border p-2 rounded w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Sponsor Name (optional)</label>
              <input
                type="text"
                name="sponsor_name"
                placeholder="Sponsor Name"
                value={formData.sponsor_name}
                onChange={handleChange}
                className="border p-2 rounded w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Event Level</label>
              <SearchableSelect
                name="event_level"
                value={formData.event_level}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                disabled={(role || "").toLowerCase() === "district"}
              >
                <option value="National">National</option>
                <option value="State">State</option>
                <option value="District">District</option>
              </SearchableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Event For Age Group</label>
              <SearchableSelect
                name="target_age_category"
                value={formData.target_age_category}
                onChange={handleChange}
                className="border p-2 rounded w-full"
              >
                <option value="all">All</option>
                <option value="sub_junior">Sub-Junior</option>
                <option value="junior">Junior</option>
                <option value="senior">Senior</option>
              </SearchableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Event For Gender</label>
              <SearchableSelect
                name="target_gender"
                value={formData.target_gender}
                onChange={handleChange}
                className="border p-2 rounded w-full"
              >
                <option value="all">All</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </SearchableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">District</label>
              <SearchableSelect
                name="district_id"
                value={formData.district_id}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                disabled={(role || "").toLowerCase() === "district"}
              >
                <option value="">Select District</option>
                {districtOptions.map((d) => (
                  <option key={`${d.id ?? "na"}-${d.name}`} value={d.id ?? ""}>
                    {d.name}
                  </option>
                ))}
              </SearchableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Max Games Per Player</label>
              <input
                type="number"
                min="1"
                max="10"
                name="max_games_per_player"
                value={formData.max_games_per_player}
                onChange={handleChange}
                className="border p-2 rounded w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Registration</label>
              <SearchableSelect name="registration" value={formData.registration} onChange={handleChange} className="border p-2 rounded w-full">
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
              </SearchableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Event Image</label>
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="mt-2 h-24 object-cover rounded border"
                />
              )}
              <input 
                type="file" 
                name="image" 
                accept="image/*" 
                onChange={handleFileChange} 
                className="border p-2 rounded w-full" 
              />
              <small className="text-xs text-gray-500">Optional. Max 5MB.</small>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Sponsor Logo</label>
              {sponsorLogoPreview && (
                <img
                  src={sponsorLogoPreview}
                  alt="Sponsor logo preview"
                  className="mt-2 h-20 object-contain rounded border bg-white p-1"
                />
              )}
              <input
                type="file"
                name="sponsor_logo"
                accept="image/*"
                onChange={handleSponsorLogoChange}
                className="border p-2 rounded w-full"
              />
              <small className="text-xs text-gray-500">Optional. Max 5MB.</small>
            </div>
          </div>

          <textarea 
            name="description" 
            placeholder="Event Description" 
            value={formData.description} 
            onChange={handleChange} 
            rows="3" 
            className="border p-2 rounded w-full mt-3"
          ></textarea>

          <div className="flex justify-end mt-4 space-x-3">
            {editing && (
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    id: null,
                    sport_id: "",
                    title: "",
                    start_date: "",
                    end_date: "",
                    date: "",
                    event_end_date: "",
                    end_time: "",
                    location: "",
                    description: "",
                    convenor_name: "",
                    sponsor_name: "",
                    registration_fee: "",
                    escort_fee: "",
                    max_games_per_player: 3,
                    event_level: "District",
                    target_age_category: "all",
                    target_gender: "all",
                    district: (role || "").toLowerCase() === "district" ? (user?.district || "") : "",
                    district_id: (role || "").toLowerCase() === "district" ? String(user?.district_id || "") : "",
                    registration: "Closed",
                    image: null,
                    sponsor_logo: null,
                  });
                  setImagePreview(null);
                  setSponsorLogoPreview(null);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
              >
                Cancel
              </button>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 rounded text-white ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {loading ? "Saving..." : (editing ? "Update Event" : "Add Event")}
            </button>
          </div>
        </form>
        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Form mode</p>
            <p className="mt-1 text-lg font-semibold text-slate-900">
              {editing ? "Editing Existing Event" : "Creating New Event"}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Keep registration window and event dates consistent to avoid approval issues.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5">
                <p className="text-slate-500">Loading</p>
                <p className="font-semibold text-slate-800">{loading ? "Saving..." : "Ready"}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5">
                <p className="text-slate-500">District</p>
                <p className="font-semibold text-slate-800">{formData.district || "-"}</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
            <p className="text-sm font-semibold text-amber-900">Publishing Checklist</p>
            <ul className="mt-2 space-y-1.5 text-xs text-amber-900/90">
              <li>Set start/end registration dates before opening.</li>
              <li>Assign event level and district correctly.</li>
              <li>Confirm max games per player is configured.</li>
              <li>Use image/logo only when final approved assets are ready.</li>
            </ul>
          </div>
        </aside>
        </div>

        {/* 📋 Events Table */}
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-2.5">
          <h2 className="text-lg font-semibold text-slate-900">Event Library</h2>
          <p className="text-sm text-slate-600">Search, filter and operate event status from this table.</p>
        </div>
        <div className="p-2.5 sm:p-3 border-b border-slate-200">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, sport, district, level, age scope, gender scope..."
              className="border border-gray-300 rounded px-3 py-2 w-full md:max-w-md"
            />
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={showOpen}
                onChange={(e) => setShowOpen(e.target.checked)}
              />
              Open ({openCount})
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={showClosed}
                onChange={(e) => setShowClosed(e.target.checked)}
              />
              Closed ({closedCount})
            </label>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[1700px] w-full border border-gray-200 text-xs sm:text-sm">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Title</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Image</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Sport</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Level</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Age Scope</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Gender Scope</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">District</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Start Date</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">End Date</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Event Date(s)</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">End Time</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Convenor</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Sponsor</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Fee (₹)</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Max Games</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Status</th>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(filteredEvents) && filteredEvents.length > 0 ? (
                filteredEvents.map((e) => {
                  const status = normalizeRegistration(e.registration);
                  return (
                  <tr key={e.id} className="text-center hover:bg-gray-50 transition align-middle">
                    <td className="px-2.5 py-2 border border-gray-200">{e.title}</td>
                    <td className="px-2.5 py-2 border border-gray-200">
                      {e.image ? (
                        <img
                          src={resolveAssetUrl(e.image, { folder: "events" })}
                          alt={e.title}
                          className="h-9 w-auto object-cover mx-auto"
                        />
                      ) : (
                        <span className="text-xs text-gray-500">No image</span>
                      )}
                    </td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.sport_name + "-" + e.sport_id}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.event_level || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.target_age_category || "all"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.target_gender || "all"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.district || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.start_date || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.end_date || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{formatEventDateRange(e.event_date || e.date, e.event_end_date)}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{formatTime(e.end_time) || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.convenor_name || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">
                      <div className="flex flex-col items-center gap-1">
                        <span>{e.sponsor_name || "-"}</span>
                        {e.sponsor_logo ? (
                          <img
                            src={resolveAssetUrl(e.sponsor_logo, { folder: "events/sponsors" })}
                            alt={e.sponsor_name || "Sponsor"}
                            className="h-6 object-contain"
                          />
                        ) : null}
                      </div>
                    </td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.registration_fee}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.max_games_per_player ?? 3}</td>
                    <td className="px-2.5 py-2 border border-gray-200">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          status === "Open"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {status}
                      </span>
                    </td>
                    <td className="px-2.5 py-2 border border-gray-200">
                      <div className="flex items-center justify-center gap-1.5 flex-nowrap overflow-x-auto whitespace-nowrap pb-0.5">
                        <button
                          onClick={() => handleToggle(e.id, status)}
                          className={`px-2.5 py-1 rounded text-xs text-white ${
                            status === "Open"
                              ? "bg-gray-500 hover:bg-gray-600"
                              : "bg-green-600 hover:bg-green-700"
                          }`}
                        >
                          {status === "Open" ? "Close" : "Open"}
                        </button>
                        <button
                          onClick={() => handleEdit(e)}
                          className="bg-yellow-400 hover:bg-yellow-500 text-white px-2.5 py-1 rounded text-xs"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(e.id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-2.5 py-1 rounded text-xs"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
              ) : (
                <tr>
                  <td colSpan="17" className="p-2.5 text-gray-500 text-center">
                    No events found for current search/filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </section>
      </div>
    </>
  );
};

export default ManageEvents;
